import { https } from "../http";
import store from "../store";
import { setSelectedClient } from "../store/reducers/mar";

import {
  setAddPatientLoading,
  setLoading,
  setPatients,
  setAllPatients,
  setAllReportClients,
  setActivePatientsCount,
} from "../store/reducers/patient";
import { setLoadingSaveProfile } from "../store/reducers/profile";
import { decrypt, showError, showInfo } from "../utils/index";
import { getMar, clearMarCache } from "./mar";

// GET
const getAllPatients = async (page, showLoading = true, selectedTab) => {
  try {
    const strUrl = `/client/${page}`;
    let baseURL =
      selectedTab !== undefined ? `${strUrl}?status=${selectedTab}` : strUrl;
    if (showLoading) store.dispatch(setLoading(true));
    let response = await https().get(baseURL);

    if (response?.status === 200) {
      let data = decrypt(response?.data?.data);
      let patients = data?.patients?.map((pa) => {
        if (typeof pa.name === "string" && typeof pa.form1 === "string")
          return {
            ...pa,
            name: `${decrypt(pa.name, false)}`,
            form1: decrypt(pa.form1),
            form2: decrypt(pa.form2),
            form3: decrypt(pa.form3),
          };
        else return pa;
      });
      store.dispatch(setPatients({ ...data, patients }));
      store.dispatch(setActivePatientsCount(data?.activePatientsCount));
    }
  } catch (error) {
    console.log({ error });
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const getSinglePatients = async (id) => {
  if (!id || id === "undefined") {
    store.dispatch(setLoading(false));
    return null;
  }
  try {
    store.dispatch(setLoading(true));
    let response = await https().get(`/client/single/${id}`);

    if (response?.status === 200) {
      store.dispatch(setLoading(false));
      let data = decrypt(response?.data?.data, true);
      if (
        typeof data?.patient?.name === "string" &&
        typeof data?.patient?.form1 === "string"
      ) {
        let responseData = {
          ...data?.patient,
          name: `${decrypt(data?.patient?.name, false)}`,
          form1: decrypt(data?.patient?.form1),
          form2: decrypt(data?.patient?.form2),
          form3: decrypt(data?.patient?.form3),
        };

        localStorage.setItem("selectedClient", JSON.stringify(responseData));
        store.dispatch(setSelectedClient(responseData));

        return responseData;
      } else return data?.patient;
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const getAllPatientsModal = async (
  page = "all",
  showLoading = true,
  clientId = null,
) => {
  try {
    if (showLoading) store.dispatch(setLoading(true));
    let response = await https().get(`/client/${page}`);
    if (response?.status === 200) {
      let data = decrypt(response?.data?.data);
      let patients = data?.patients?.map((pa) => {
        if (typeof pa.name === "string" && typeof pa.form1 === "string")
          return {
            ...pa,
            name: decrypt(pa.name, false),
            form1: decrypt(pa.form1),
            form2: decrypt(pa.form2),
            form3: decrypt(pa.form3),
          };
        else return pa;
      });
      const _setClient = (patients) => {
        const currentlySelected = store.getState().mar.selectedClient;
        // Only auto-select when no client is currently active in Redux.
        // Overwriting an already-selected client here was the root cause of
        // OD-1073: getAllPatientsModal completing asynchronously would silently
        // switch the active client to patients[0] mid-session, causing MAR week
        // navigation to fetch and display a different client's medications.
        if (currentlySelected?.id) return;
        const _localClient = JSON.parse(localStorage.getItem("selectedClient"));
        store.dispatch(
          setSelectedClient(
            patients?.length > 0
              ? _localClient
                ? _localClient
                : patients?.[0]
              : null,
          ),
        );
      };

      if (clientId) {
        let patient = patients?.find((obj) => obj.id === clientId);
        if (patient) {
          localStorage.setItem("selectedClient", JSON.stringify(patient));
          store.dispatch(setSelectedClient(patient));
        } else {
          _setClient(patients);
        }
      } else {
        _setClient(patients);
      }
      store.dispatch(setAllPatients(patients));
      // if (page === "all") {
      //   store.dispatch(setAllAssignPatients(patients));
      //   store.dispatch(setAllPatients(patients));
      // } else {
      //   store.dispatch(setAllPatients(patients));
      // }
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// Client list for the REPORTING pickers: active + discharged (active: false),
// deleted excluded. Kept separate from `allPatients` on purpose — `allPatients`
// is what note creation / MAR / task assignment read, and those must never
// offer a discharged client as a target for new data. Reports are the opposite
// case: a note written while the client was active has to keep appearing in
// reports covering that period after the client is discharged (OD-1294).
// Deliberately does NOT touch the shared `loading` flag — it runs alongside the
// normal client fetch and would otherwise fight it over the same spinner.
const getReportClients = async () => {
  try {
    const response = await https().get("/client/allWithInactive");
    if (response?.status === 200) {
      const data = decrypt(response?.data?.data);
      const patients = data?.patients?.map((pa) => {
        if (typeof pa.name === "string" && typeof pa.form1 === "string")
          return {
            ...pa,
            name: decrypt(pa.name, false),
            form1: decrypt(pa.form1),
            form2: decrypt(pa.form2),
            form3: decrypt(pa.form3),
          };
        else return pa;
      });
      store.dispatch(setAllReportClients(patients || []));
      return patients || [];
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
  return [];
};

// POST
const addPatient = async (data, tabName) => {
  try {
    store.dispatch(setAddPatientLoading(true));
    let response = await https().post("/client", data);

    if (response?.status === 200) {
      store.dispatch(setAddPatientLoading(false));
      getAllPatients(1, true, tabName);
      return response;
    }
  } catch (error) {
    store.dispatch(setAddPatientLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// PUT
const editPatient = async (
  data,
  showLoading = true,
  page = undefined,
  selectedTab,
) => {
  try {
    if (showLoading) store.dispatch(setAddPatientLoading(true));

    let response = await https().put(`/client/${data?.id}`, data);

    if (response?.status === 200) {
      getAllPatients(1, true, selectedTab);
    }
    return response;
  } catch (error) {
    store.dispatch(setAddPatientLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// update staff status

const updateClientStatus = async (data, showLoading = true, selectedTab) => {
  const { id, active } = data;
  try {
    store.dispatch(setAddPatientLoading(true));
    let response = await https().post(`/client/update-status/${id}`, {
      status: active,
      data: data,
    });
    if (response?.status === 200) {
      getAllPatients(1, true, selectedTab);
      return true;
    }
  } catch (error) {
    store.dispatch(setAddPatientLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const editPatientFacilitiy = async (patientId, payload, showLoading = true) => {
  try {
    // if (showLoading) store.dispatch(setAddPatientLoading(true));

    let response = await https().post(
      `/client/update-patient-facility/${patientId}`,
      payload,
    );

    if (response?.status === 200) {
      //store.dispatch(setAddPatientLoading(false));
      // getAllPatients(page, showLoading);
    }
    return response;
  } catch (error) {
    store.dispatch(setAddPatientLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const AddMedication = async (
  data,
  id,
  showLoading = true,
  page = undefined,
  reset,
  showSwal = true,
) => {
  console.log("🚀 ~ data:", data);
  try {
    if (showLoading) store.dispatch(setAddPatientLoading(true));

    const isArray = Array.isArray(data);
    let response = await https().put(
      `/client/addmedication/${id}`,
      data,
      isArray ? { headers: { "Content-Type": "application/json" } } : undefined
    );

    if (response?.status === 200) {
      {
        showSwal && showInfo(response?.data?.message);
      }
      // getAllPatientsModal(true, true, id);
      getAllPatientsModal("all", true, id);
      // getAllPatientsModal();
      getMar(localStorage.getItem("calendar_date"), id);
      reset?.();
      //store.dispatch(setAddPatientLoading(false));
      // getAllPatients(page, showLoading);
    }
  } catch (error) {
    store.dispatch(setAddPatientLoading(false));
    console.log("error", error);
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const EditMedication = async (data, id, showLoading = true, reset) => {
  try {
    if (showLoading) store.dispatch(setAddPatientLoading(true));

    let response = await https().put(`/client/editmedication/${id}`, data);

    if (response?.status === 200) {
      showInfo(response?.data?.message);
      // getAllPatientsModal(true, id);
      // getAllPatientsModal();
      getAllPatientsModal("all", true, id);
      clearMarCache();
      getMar(localStorage.getItem("calendar_date"), id);
      reset?.();
    }
  } catch (error) {
    store.dispatch(setAddPatientLoading(false));
    console.log("error", error);
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const deleteMedication = async (payload) => {
  try {
    store.dispatch(setAddPatientLoading(true));
    let string = `/client/deletemedication`;
    let response = await https().post(string, payload);
    if (response?.status === 200) {
      getAllPatientsModal("all", true, payload?.clientId);
      clearMarCache();
      getMar(localStorage.getItem("calendar_date"), payload?.clientId);
    }

    store.dispatch(setAddPatientLoading(false));
  } catch (error) {
    store.dispatch(setAddPatientLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// update cliet photo

const updateclientprofile = async (data, id) => {
  try {
    store.dispatch(setLoadingSaveProfile(true));
    let response = await https().put(`/client/profilephoto/${id}`, data);

    if (response?.status === 200) {
      //getProfile();
      return response?.data;
    }
  } catch (error) {
    let _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
    store.dispatch(setLoadingSaveProfile(false));
    return false;
  }
};

const uploadClientProfile = async (data) => {
  try {
    store.dispatch(setLoadingSaveProfile(true));
    let response = await https().post(`/client/profilephoto`, data);

    if (response?.status === 200) {
      //getProfile();
      return response?.data;
    }
  } catch (error) {
    let _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
    store.dispatch(setLoadingSaveProfile(false));
    return false;
  }
};

const EditclientProfile = async (payload) => {
  try {
    await https().put(`/client/updateprofile`, payload);
  } catch (error) {
    console.log("🚀 ~ EditclientProfile ~ error:", error);
  }
};

// DELETE
const removePatient = async (id, callback, selectedTab) => {
  try {
    let response = await https().delete(`/client/${id}`);

    if (response?.status === 200) {
      getAllPatients(1, true, selectedTab);
      callback?.();
    }
  } catch (error) {
    store.dispatch(setAddPatientLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// attach payer to client

const attachpayertoclient = async (payload) => {
  try {
    let response = await https().post("/client/attach-payer-client", payload);

    return response;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

export {
  addPatient,
  editPatient,
  getAllPatients,
  uploadClientProfile,
  updateclientprofile,
  updateClientStatus,
  AddMedication,
  removePatient,
  EditclientProfile,
  getAllPatientsModal,
  getReportClients,
  getSinglePatients,
  EditMedication,
  deleteMedication,
  editPatientFacilitiy,
  attachpayertoclient,
};
